import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, Search, X } from "lucide-react";
import { supabase } from "../supabaseClient";
import AdminTopBar from "./AdminTopBar";
import AdminPagination from "./AdminPagination";
import { BusyTable } from "../components/TableBusyOverlay";
import usePagedListUi from "../hooks/usePagedListUi";
import { ADMIN_PAGE_SIZE, applyPagedSelect, totalPagesFromCount, uniqueIds } from "../lib/adminPagination";
import {
  adminCard,
  adminEmpty,
  adminPageBg,
  adminPageTitle,
  adminPrimaryBtn,
  adminRow,
  adminSearchInput,
  adminSearchWrap,
  adminSecondaryBtn,
  adminStatusPill,
  adminTableHead,
  adminTableWrap,
  adminTd,
  adminTdName,
} from "./adminUi";
import SuccessToast from "../components/SuccessToast";
import { formatManualTicketId } from "../components/TicketReceiptCard";
import { formatExactSection, resolveStoredSeatArea } from "../lib/seatSectionDisplay";
import { getPaymentServiceBase } from "../lib/paymentApiUrl";
import {
  buildOrdersCsv,
  buildOrdersReportPdf,
  defaultLast30ManilaDays,
  downloadTextFile,
  filterOrdersByManilaRange,
  ordersReportFilename,
  summarizeOrders,
  PDF_DETAIL_ROW_LIMIT,
} from "../lib/adminOrdersReport";
import { createAdminNotification } from "./notificationsService";
import {
  ORDER_STATUS_OPTIONS,
  formatDateKey,
  formatMoney,
  formatOrderDate,
  isPaidOrder,
  isPendingOrder,
  isRefundedOrder,
  mapPaymentOrder,
  releaseOrderSeatHolds,
  statusClassName,
  statusLabel,
} from "./ordersPage";
import {
  denyRefundRequest,
  listOpenRefundRequestsForOrders,
  markOrderRefunded,
} from "../lib/refundOrders";
import {
  SUPPORT_EMAIL_TYPES,
  notifyBuyerRefundUpdate,
  resolveBuyerEmailForNotify,
} from "../lib/supportEmailNotify";

const PAGE_SIZE = ADMIN_PAGE_SIZE;

const isMissingColumn = (error, columnName) => {
  if (!error) return false;
  const code = String(error.code || "");
  const message = String(error.message || "").toLowerCase();
  return code === "42703" || message.includes(String(columnName || "").toLowerCase());
};

const isRlsOrPermissionError = (error) => {
  if (!error) return false;
  const code = String(error.code || "");
  const message = String(error.message || "").toLowerCase();
  return (
    code === "42501"
    || message.includes("permission denied")
    || message.includes("row-level security")
    || message.includes("rls")
  );
};

const formatTicketSection = (tier, ticket) => formatExactSection(
  tier?.section || tier?.name || "",
  resolveStoredSeatArea({
    seatArea: ticket.seat_area,
    seatLabel: ticket.seat_label,
  })
);

const ORDERS_SELECT_PAID =
  "id, user_id, event_id, amount, status, selected_tickets, paid_at, created_at, updated_at";
const ORDERS_SELECT_BASE =
  "id, user_id, event_id, amount, status, selected_tickets, created_at, updated_at";

const loadTicketsForOrderIds = async (orderIds) => {
  if (!orderIds.length) return { data: [], error: null };

  const ticketsSelectWithOrder =
    "id, buyer_id, ticket_tier_id, created_at, seat_label, seat_area, payment_order_id, is_validated, refunded_at";

  let ticketsResult = await supabase
    .from("tickets")
    .select(ticketsSelectWithOrder)
    .in("payment_order_id", orderIds);

  if (isMissingColumn(ticketsResult.error, "payment_order_id")) {
    return { data: [], error: null };
  }
  if (isMissingColumn(ticketsResult.error, "refunded_at")) {
    ticketsResult = await supabase
      .from("tickets")
      .select("id, buyer_id, ticket_tier_id, created_at, seat_label, seat_area, payment_order_id, is_validated")
      .in("payment_order_id", orderIds);
  } else if (ticketsResult.error && isMissingColumn(ticketsResult.error, "seat_area")) {
    ticketsResult = await supabase
      .from("tickets")
      .select("id, buyer_id, ticket_tier_id, created_at, seat_label, payment_order_id, is_validated")
      .in("payment_order_id", orderIds);
  }

  return ticketsResult;
};

const normalizePaymentOrders = ({ paymentOrders, ticketsData, tiersData, eventsData, usersData }) => {
  const tiersById = {};
  (tiersData || []).forEach((tier) => {
    tiersById[tier.id] = tier;
  });

  const eventsById = {};
  (eventsData || []).forEach((event) => {
    eventsById[event.id] = event;
  });

  const usersById = {};
  (usersData || []).forEach((user) => {
    usersById[user.id] = user;
  });

  const ticketsByOrderId = new Map();
  (ticketsData || []).forEach((ticket) => {
    const orderId = ticket.payment_order_id;
    if (!orderId) return;
    if (!ticketsByOrderId.has(orderId)) ticketsByOrderId.set(orderId, []);
    ticketsByOrderId.get(orderId).push(ticket);
  });

  return (paymentOrders || []).map((order) => mapPaymentOrder({
    order,
    event: eventsById[order.event_id],
    buyer: usersById[order.user_id],
    tickets: ticketsByOrderId.get(order.id) || [],
    tiersById,
    formatTicketId: formatManualTicketId,
    formatSection: formatTicketSection,
  })).sort((left, right) => {
    const leftTime = new Date(left.purchaseDate || 0).getTime();
    const rightTime = new Date(right.purchaseDate || 0).getTime();
    return rightTime - leftTime;
  });
};

const Orders = () => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [dataError, setDataError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [eventFilter, setEventFilter] = useState("All Events");
  const [dateFilter, setDateFilter] = useState("All Dates");
  const [currentPage, setCurrentPage] = useState(() => {
    if (typeof window === "undefined") return 1;
    const parsed = Number(new URLSearchParams(window.location.search).get("page"));
    return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [eventCatalog, setEventCatalog] = useState([]);
  const [exportSourceOrders, setExportSourceOrders] = useState([]);
  const listTopRef = useRef(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [isDenyingRefund, setIsDenyingRefund] = useState(false);
  const [refundConfirmAck, setRefundConfirmAck] = useState(false);
  const [denyNote, setDenyNote] = useState("");
  const [refundActionConfirm, setRefundActionConfirm] = useState(null); // "mark" | "deny" | null
  const [refundRequestsByOrderId, setRefundRequestsByOrderId] = useState({});
  const [pendingDeepLinkOrderId, setPendingDeepLinkOrderId] = useState(() => {
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams(window.location.search);
    return String(params.get("order") || params.get("orderId") || "").trim();
  });
  const [toastMessage, setToastMessage] = useState("");
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [exportAllDates, setExportAllDates] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");

  const showSuccessToast = (message) => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(""), 2500);
  };

  const showErrorToast = (message) => {
    setErrorToastMessage(message);
    window.setTimeout(() => setErrorToastMessage(""), 3500);
  };

  const fetchOrders = useCallback(async () => {
    try {
      await fetch(`${getPaymentServiceBase()}/expire-stale-checkouts`, { method: "POST" });
    } catch (error) {
      console.warn("Unable to expire stale pending orders:", error);
    }

    setLoading(true);

    const { data: eventsData, error: eventsError } = await supabase
      .from("events")
      .select("id, title, date, time, venue")
      .order("date", { ascending: false });

    if (!eventsError) {
      setEventCatalog(eventsData || []);
    }

    const needle = search.trim();
    let matchedUserIds = [];
    let matchedEventIds = [];
    if (needle) {
      const like = `%${needle}%`;
      const [{ data: matchedUsers }, { data: matchedEvents }] = await Promise.all([
        supabase.from("users").select("id").or(`full_name.ilike.${like},email.ilike.${like}`).limit(80),
        supabase.from("events").select("id").ilike("title", like).limit(80),
      ]);
      matchedUserIds = uniqueIds(matchedUsers, "id");
      matchedEventIds = uniqueIds(matchedEvents, "id");
    }

    const eventIdForFilter = eventFilter === "All Events"
      ? ""
      : String((eventsData || []).find((event) => event.title === eventFilter)?.id || "");

    const buildOrdersQuery = (selectCols) => {
      let query = supabase
        .from("payment_orders")
        .select(selectCols, { count: "exact" })
        .order("created_at", { ascending: false });

      if (statusFilter !== "All") {
        query = query.eq("status", statusFilter);
      }
      if (eventIdForFilter) {
        query = query.eq("event_id", eventIdForFilter);
      }
      if (dateFilter !== "All Dates") {
        query = query
          .gte("created_at", `${dateFilter}T00:00:00+08:00`)
          .lt("created_at", `${dateFilter}T23:59:59.999+08:00`);
      }
      if (needle) {
        const clauses = [`id.eq.${needle}`];
        if (matchedUserIds.length) clauses.push(`user_id.in.(${matchedUserIds.join(",")})`);
        if (matchedEventIds.length) clauses.push(`event_id.in.(${matchedEventIds.join(",")})`);
        query = query.or(clauses.join(","));
      }

      return applyPagedSelect(query, currentPage, PAGE_SIZE);
    };

    let ordersResult = await buildOrdersQuery(ORDERS_SELECT_PAID);
    if (isMissingColumn(ordersResult.error, "paid_at")) {
      ordersResult = await buildOrdersQuery(ORDERS_SELECT_BASE);
    }

    const { data: paymentOrders, error: ordersError, count } = ordersResult;

    if (ordersError) {
      console.error("Failed to load payment orders:", ordersError);
      setDataError(
        isRlsOrPermissionError(ordersError)
          ? "Order data could not be loaded. Run payment_orders_admin_read.sql in Supabase, then sign in as admin and refresh."
          : `Order data could not be loaded: ${ordersError.message || "Unknown error"}`
      );
      setOrders([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    const orderIds = uniqueIds(paymentOrders, "id");
    const ticketsResult = await loadTicketsForOrderIds(orderIds);
    const { data: ticketsData, error: ticketsError } = ticketsResult;
    const tierIds = uniqueIds(ticketsData, "ticket_tier_id");
    const userIds = uniqueIds(paymentOrders, "user_id");
    const pageEventIds = uniqueIds(paymentOrders, "event_id");

    const [
      { data: tiersData, error: tiersError },
      { data: pageEventsData, error: pageEventsError },
      { data: usersData, error: usersError },
    ] = await Promise.all([
      tierIds.length
        ? supabase.from("ticket_tiers").select("id, event_id, name, price, section").in("id", tierIds)
        : Promise.resolve({ data: [], error: null }),
      pageEventIds.length
        ? supabase.from("events").select("id, title, date, time, venue").in("id", pageEventIds)
        : Promise.resolve({ data: [], error: null }),
      userIds.length
        ? supabase.from("users").select("id, full_name, email").in("id", userIds)
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (ticketsError || tiersError || eventsError || pageEventsError) {
      const blockingError = ticketsError || tiersError || eventsError || pageEventsError;
      console.error("Failed to load related order data:", blockingError);
      setDataError(`Order details could not be loaded: ${blockingError?.message || "Unknown error"}`);
    } else if (usersError) {
      console.warn("Users lookup blocked while loading orders:", usersError);
      setDataError(`Limited data access: ${usersError.message}. Orders are shown, customer names may be unavailable.`);
    } else {
      setDataError("");
    }

    const normalized = normalizePaymentOrders({
      paymentOrders,
      ticketsData,
      tiersData,
      eventsData: pageEventsData?.length ? pageEventsData : eventsData,
      usersData,
    });

    setOrders(normalized);
    setTotalCount(Number(count) || normalized.length);
    setExportSourceOrders(normalized);

    try {
      const requestRows = await listOpenRefundRequestsForOrders(normalized.map((order) => order.id));
      const byOrder = {};
      (Array.isArray(requestRows) ? requestRows : []).forEach((row) => {
        const orderId = row.payment_order_id;
        if (!orderId || byOrder[orderId]) return;
        byOrder[orderId] = {
          id: row.id,
          ticketCode: row.ticket_code,
          subject: row.subject,
          description: row.description,
          status: row.status,
        };
      });
      setRefundRequestsByOrderId(byOrder);
    } catch (requestError) {
      console.warn("Unable to load open refund requests:", requestError);
      setRefundRequestsByOrderId({});
    }

    setLoading(false);
  }, [currentPage, dateFilter, eventFilter, search, statusFilter]);

  useEffect(() => {
    fetchOrders();

    const ordersChannel = supabase
      .channel("orders-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "payment_orders" }, fetchOrders)
      .on("postgres_changes", { event: "*", schema: "public", table: "tickets" }, fetchOrders)
      .on("postgres_changes", { event: "*", schema: "public", table: "ticket_tiers" }, fetchOrders)
      .on("postgres_changes", { event: "*", schema: "public", table: "events" }, fetchOrders)
      .on("postgres_changes", { event: "*", schema: "public", table: "users" }, fetchOrders)
      .on("postgres_changes", { event: "*", schema: "public", table: "support_tickets" }, fetchOrders)
      .subscribe();

    return () => {
      ordersChannel.unsubscribe();
    };
  }, [fetchOrders]);

  useEffect(() => {
    if (!selectedOrder) return;
    const next = orders.find((order) => order.id === selectedOrder.id);
    if (next && next !== selectedOrder) {
      setSelectedOrder(next);
    }
  }, [orders, selectedOrder]);

  useEffect(() => {
    if (!pendingDeepLinkOrderId || loading || !orders.length) return;
    const match = orders.find((order) => String(order.id) === String(pendingDeepLinkOrderId));
    if (match) {
      setSelectedOrder(match);
      setPendingDeepLinkOrderId("");
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        url.searchParams.delete("order");
        url.searchParams.delete("orderId");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      }
    } else {
      setPendingDeepLinkOrderId("");
    }
  }, [pendingDeepLinkOrderId, loading, orders]);

  const eventOptions = useMemo(() => {
    const unique = new Set(eventCatalog.map((item) => item.title).filter(Boolean));
    return ["All Events", ...Array.from(unique)];
  }, [eventCatalog]);

  const dateOptions = useMemo(() => {
    const days = [{ value: "All Dates", label: "Date" }];
    for (let index = 0; index < 30; index += 1) {
      const stamp = new Date();
      stamp.setDate(stamp.getDate() - index);
      const key = formatDateKey(stamp.toISOString());
      if (!key) continue;
      days.push({ value: key, label: formatOrderDate(key) });
    }
    return days;
  }, []);

  const paidInView = useMemo(() => summarizeOrders(orders), [orders]);
  const totalPages = totalPagesFromCount(totalCount, PAGE_SIZE);
  const paginatedOrders = orders;
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, eventFilter, dateFilter]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("page", String(currentPage));
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [currentPage]);

  const closeOrderModal = () => {
    if (isCancelling || isRefunding || isDenyingRefund) return;
    setSelectedOrder(null);
    setRefundConfirmAck(false);
    setDenyNote("");
    setRefundActionConfirm(null);
  };

  const selectedRefundRequest = selectedOrder
    ? refundRequestsByOrderId[selectedOrder.id] || null
    : null;
  const selectedHasUsedTickets = Boolean(
    (selectedOrder?.ticketDetails || []).some((ticket) => ticket.isValidated)
  );

  const requestMarkOrderRefunded = () => {
    if (!selectedOrder || !isPaidOrder(selectedOrder.status) || isRefunding) return;
    if (selectedHasUsedTickets && !refundConfirmAck) {
      showErrorToast("Confirm that used tickets may still be refunded.");
      return;
    }
    setRefundActionConfirm("mark");
  };

  const requestDenyRefundRequest = () => {
    if (!selectedOrder || !selectedRefundRequest || isDenyingRefund) return;
    if (!denyNote.trim()) {
      showErrorToast("Add a note before denying this refund request.");
      return;
    }
    setRefundActionConfirm("deny");
  };

  const confirmMarkOrderRefunded = async () => {
    if (!selectedOrder || !isPaidOrder(selectedOrder.status) || isRefunding) return;

    setIsRefunding(true);
    try {
      await markOrderRefunded(selectedOrder.id, "Order marked refunded in admin Orders.");
      await createAdminNotification({
        title: "Order refunded",
        message: `${selectedOrder.orderCode} marked refunded. Complete money return in PayMongo if needed.`,
        meta: "Orders",
      });

      const toEmail = await resolveBuyerEmailForNotify({
        userId: selectedOrder.userId,
        fallbackEmail: selectedOrder.customerEmail,
      });
      const notifyResult = await notifyBuyerRefundUpdate({
        type: SUPPORT_EMAIL_TYPES.REFUND_APPROVED,
        toEmail,
        orderId: selectedOrder.id,
        orderCode: selectedOrder.orderCode,
        eventTitle: selectedOrder.event,
        supportTicketId: selectedRefundRequest?.id || null,
        paymentOrderId: selectedOrder.id,
      }).catch((notifyError) => {
        console.error("Refund approved email failed:", notifyError);
        return { ok: false, error: notifyError.message || "Unable to send refund email." };
      });
      if (!notifyResult?.ok) {
        showErrorToast(notifyResult?.skipped
          ? "Order refunded, but buyer email is missing so no email was sent."
          : `Order refunded, but email failed: ${notifyResult?.error || "check function logs."}`);
      }

      showSuccessToast(`${selectedOrder.orderCode} marked refunded.`);
      setRefundConfirmAck(false);
      setRefundActionConfirm(null);
      await fetchOrders();
    } catch (error) {
      console.error("Failed to mark order refunded:", error);
      showErrorToast(error.message || "Unable to mark this order refunded.");
    } finally {
      setIsRefunding(false);
    }
  };

  const confirmDenyRefundRequest = async () => {
    if (!selectedOrder || !selectedRefundRequest || isDenyingRefund) return;
    const note = denyNote.trim();
    if (!note) {
      showErrorToast("Add a note before denying this refund request.");
      return;
    }

    setIsDenyingRefund(true);
    try {
      await denyRefundRequest(selectedRefundRequest.id, note);

      const toEmail = await resolveBuyerEmailForNotify({
        userId: selectedOrder.userId,
        fallbackEmail: selectedOrder.customerEmail,
      });
      const notifyResult = await notifyBuyerRefundUpdate({
        type: SUPPORT_EMAIL_TYPES.REFUND_DENIED,
        toEmail,
        orderId: selectedOrder.id,
        orderCode: selectedOrder.orderCode,
        eventTitle: selectedOrder.event,
        adminNote: note,
        supportTicketId: selectedRefundRequest.id,
        paymentOrderId: selectedOrder.id,
      }).catch((notifyError) => {
        console.error("Refund denied email failed:", notifyError);
        return { ok: false, error: notifyError.message || "Unable to send refund email." };
      });
      if (!notifyResult?.ok) {
        showErrorToast(notifyResult?.skipped
          ? "Request denied, but buyer email is missing so no email was sent."
          : `Request denied, but email failed: ${notifyResult?.error || "check function logs."}`);
      }

      showSuccessToast(`Refund request ${selectedRefundRequest.ticketCode} denied.`);
      setDenyNote("");
      setRefundActionConfirm(null);
      await fetchOrders();
    } catch (error) {
      console.error("Failed to deny refund request:", error);
      showErrorToast(error.message || "Unable to deny this refund request.");
    } finally {
      setIsDenyingRefund(false);
    }
  };

  const openExportModal = () => {
    const range = defaultLast30ManilaDays();
    setExportFrom(range.from);
    setExportTo(range.to);
    setExportAllDates(false);
    setExportFormat("csv");
    setShowExportModal(true);
  };

  const closeExportModal = () => {
    setShowExportModal(false);
  };

  const downloadOrdersReport = () => {
    let fromKey = exportAllDates ? "" : exportFrom;
    let toKey = exportAllDates ? "" : exportTo;

    if (!exportAllDates) {
      if (!fromKey || !toKey) {
        showErrorToast("Choose a from and to date, or use all dates.");
        return;
      }
      if (fromKey > toKey) {
        const swapped = fromKey;
        fromKey = toKey;
        toKey = swapped;
        setExportFrom(fromKey);
        setExportTo(toKey);
      }
    }

    const rows = filterOrdersByManilaRange(exportSourceOrders, fromKey, toKey);
    const summary = summarizeOrders(rows);
    const filename = ordersReportFilename(fromKey, toKey, exportFormat);

    try {
      if (exportFormat === "pdf") {
        const doc = buildOrdersReportPdf({
          orders: rows,
          summary,
          fromKey,
          toKey,
        });
        doc.save(filename);
        if (rows.length > PDF_DETAIL_ROW_LIMIT) {
          showSuccessToast("PDF summary downloaded. Use CSV for the full order list.");
        } else {
          showSuccessToast("Orders PDF downloaded.");
        }
      } else {
        const csv = buildOrdersCsv({
          orders: rows,
          summary,
          fromKey,
          toKey,
        });
        downloadTextFile(csv, filename);
        showSuccessToast("Orders CSV downloaded.");
      }
      setShowExportModal(false);
    } catch (error) {
      console.error("Unable to export orders report:", error);
      showErrorToast("Unable to export this report. Please try again.");
    }
  };

  const cancelPendingCheckout = async () => {
    if (!selectedOrder || !isPendingOrder(selectedOrder.status) || isCancelling) return;

    setIsCancelling(true);
    try {
      const response = await fetch(`${getPaymentServiceBase()}/cancel-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payment_order_id: selectedOrder.id }),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.error || "Unable to cancel this order.");
      }

      const holdRelease = await releaseOrderSeatHolds(supabase, selectedOrder);
      if (!holdRelease.ok) {
        console.error("Order cancelled, but checkout seats were not released:", holdRelease.error);
      }

      await createAdminNotification({
        title: "Order cancelled",
        message: `${selectedOrder.orderCode} was cancelled before payment.`,
        meta: "Orders",
      });
      showSuccessToast(`${selectedOrder.orderCode} cancelled.`);
      await fetchOrders();
    } catch (error) {
      console.error("Failed to cancel payment order:", error);
      showErrorToast(error.message || "Unable to cancel this order.");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className={adminPageBg}>
      <AdminTopBar />

      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto min-w-0">
          <SuccessToast message={toastMessage} />
          <SuccessToast message={errorToastMessage} variant="error" />

          {dataError && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
              {dataError}
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-5">
            <div>
              <h1 className={adminPageTitle}>
                Orders
              </h1>
              <p className="text-xs text-gray-500 mt-1">
                Paid in view: {paidInView.paidOrderCount} order{paidInView.paidOrderCount === 1 ? "" : "s"} · {formatMoney(paidInView.paidRevenue)}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <button
                type="button"
                onClick={openExportModal}
                className={`${adminSecondaryBtn} h-9 gap-1.5`}
              >
                <Download size={14} />
                Export
              </button>
              <div className={adminSearchWrap}>
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search order, customer, or event..."
                  className={adminSearchInput}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-700 w-full sm:w-auto sm:min-w-[110px]"
            >
              {ORDER_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-700 w-full sm:w-auto sm:min-w-[150px]"
            >
              {eventOptions.map((eventName) => (
                <option key={eventName} value={eventName}>{eventName}</option>
              ))}
            </select>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-700 w-full sm:w-auto sm:min-w-[130px]"
            >
              {dateOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className={adminCard} ref={listTopRef}>
          <BusyTable show={showSkeleton} tone="admin">
          <div className={adminTableWrap}>
            <table className="w-full min-w-[980px] border-collapse">
              <thead>
                <tr className={adminTableHead}>
                  <th className="px-4 py-3 text-left">Order ID</th>
                  <th className="px-4 py-3 text-left">Customer Name</th>
                  <th className="px-4 py-3 text-left">Event Name</th>
                  <th className="px-4 py-3 text-left">Purchase Date</th>
                  <th className="px-4 py-3 text-left">Quantity</th>
                  <th className="px-4 py-3 text-left">Total</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {paginatedOrders.length === 0 ? (
                  <tr>
                    <td colSpan="8" className={adminEmpty}>{loading ? "Loading..." : "No orders found."}</td>
                  </tr>
                ) : (
                  paginatedOrders.map((order) => (
                    <tr key={order.id} className={adminRow}>
                      <td className={adminTdName}>{order.orderCode}</td>
                      <td className={adminTd}>{order.customer}</td>
                      <td className={adminTd}>{order.event}</td>
                      <td className={adminTd}>{formatOrderDate(order.purchaseDate)}</td>
                      <td className={adminTd}>{order.quantity}</td>
                      <td className={adminTd}>{formatMoney(order.total)}</td>
                      <td className="px-4 py-4">
                        <span className={`${adminStatusPill} ${statusClassName(order.status)}`}>
                          {statusLabel(order.status)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => setSelectedOrder(order)}
                          className={adminPrimaryBtn}
                          title="View order details"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </BusyTable>

          <AdminPagination
            className="mt-6"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
          </div>

          {showExportModal && (
            <div className="fixed inset-0 z-50 bg-black/50 p-4 overflow-y-auto" onClick={closeExportModal}>
              <div
                className="mx-auto w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">Export</p>
                    <h2 className="text-lg font-black text-gray-900 mt-0.5">Orders Report</h2>
                  </div>
                  <button
                    type="button"
                    onClick={closeExportModal}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="p-5 space-y-4">
                  <p className="text-xs text-gray-500">
                    Paid revenue uses completed checkouts in this range. Status and event filters on the page still apply. The range here replaces the single-day date dropdown.
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs text-gray-600">
                      From
                      <input
                        type="date"
                        value={exportFrom}
                        onChange={(e) => {
                          setExportAllDates(false);
                          setExportFrom(e.target.value);
                        }}
                        disabled={exportAllDates}
                        className="mt-1 h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-700 disabled:bg-gray-100"
                      />
                    </label>
                    <label className="text-xs text-gray-600">
                      To
                      <input
                        type="date"
                        value={exportTo}
                        onChange={(e) => {
                          setExportAllDates(false);
                          setExportTo(e.target.value);
                        }}
                        disabled={exportAllDates}
                        className="mt-1 h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-700 disabled:bg-gray-100"
                      />
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={() => setExportAllDates((prev) => !prev)}
                    className={`${adminSecondaryBtn} h-8`}
                  >
                    {exportAllDates ? "Using all dates" : "Use all dates"}
                  </button>

                  <fieldset className="space-y-2">
                    <legend className="text-xs font-semibold text-gray-700">Format</legend>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="orders-export-format"
                        checked={exportFormat === "csv"}
                        onChange={() => setExportFormat("csv")}
                      />
                      CSV (summary + order rows)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="orders-export-format"
                        checked={exportFormat === "pdf"}
                        onChange={() => setExportFormat("pdf")}
                      />
                      PDF summary
                    </label>
                  </fieldset>
                </div>

                <div className="flex gap-2 justify-end border-t border-gray-200 px-5 py-3">
                  <button type="button" onClick={closeExportModal} className={adminSecondaryBtn}>
                    Cancel
                  </button>
                  <button type="button" onClick={downloadOrdersReport} className={adminPrimaryBtn}>
                    Download
                  </button>
                </div>
              </div>
            </div>
          )}

          {selectedOrder && (
            <div className="fixed inset-0 z-50 bg-black/50 p-4 overflow-y-auto" onClick={closeOrderModal}>
              <div
                className="mx-auto w-full max-w-[92vw] sm:max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">Order Details</p>
                    <h2 className="text-lg font-black text-gray-900 mt-0.5">{selectedOrder.orderCode}</h2>
                    <p className="text-[11px] text-gray-400 mt-0.5">{selectedOrder.id}</p>
                  </div>
                  <button
                    type="button"
                    onClick={closeOrderModal}
                    disabled={isCancelling}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-50"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="p-5 space-y-5">
                  <section>
                    <h3 className="text-xs uppercase tracking-widest text-gray-500 font-black mb-2">Customer</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <p className="text-gray-800"><span className="text-gray-500">Name:</span> {selectedOrder.customer}</p>
                      <p className="text-gray-800"><span className="text-gray-500">Email:</span> {selectedOrder.customerEmail}</p>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-xs uppercase tracking-widest text-gray-500 font-black mb-2">Order Summary</h3>
                    <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                      <div className="flex justify-between px-4 py-2 text-sm">
                        <span className="text-gray-500">Event</span>
                        <span className="font-semibold text-gray-900">{selectedOrder.event}</span>
                      </div>
                      <div className="flex justify-between px-4 py-2 text-sm">
                        <span className="text-gray-500">Date</span>
                        <span className="font-semibold text-gray-900">{selectedOrder.eventDate || "-"} {selectedOrder.eventTime || ""}</span>
                      </div>
                      <div className="flex justify-between px-4 py-2 text-sm">
                        <span className="text-gray-500">Venue</span>
                        <span className="font-semibold text-gray-900">{selectedOrder.eventVenue || "-"}</span>
                      </div>
                      <div className="flex justify-between px-4 py-2 text-sm">
                        <span className="text-gray-500">Purchased</span>
                        <span className="font-semibold text-gray-900">{formatOrderDate(selectedOrder.purchaseDate)}</span>
                      </div>
                      <div className="flex justify-between px-4 py-2 text-sm">
                        <span className="text-gray-500">Quantity</span>
                        <span className="font-semibold text-gray-900">{selectedOrder.quantity}</span>
                      </div>
                      <div className="flex justify-between px-4 py-3 text-sm">
                        <span className="text-gray-500">Total</span>
                        <span className="font-black text-gray-900">{formatMoney(selectedOrder.total)}</span>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-xs uppercase tracking-widest text-gray-500 font-black mb-2">Ticket Details</h3>
                    {(selectedOrder.ticketDetails || []).length === 0 ? (
                      <p className="text-sm text-gray-500">
                        {isPendingOrder(selectedOrder.status)
                          ? "Tickets will appear after payment is completed."
                          : "No tickets were issued for this order."}
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                        {selectedOrder.ticketDetails.map((ticket, index) => (
                          <div key={ticket.id} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                            <p className="text-xs font-black text-gray-900">Ticket #{index + 1}</p>
                            <p className="text-[11px] text-gray-700">{ticket.ticketCode}</p>
                            <p className="text-[10px] text-gray-500">Manual entry if QR fails</p>
                            <p className="text-[11px] text-gray-700">{ticket.tierName}</p>
                            {ticket.exactSection ? (
                              <p className="text-[11px] text-gray-700">Section: {ticket.exactSection}</p>
                            ) : null}
                            <p className="text-[11px] text-gray-700">Row - Seat: {ticket.seatLabel || "-"}</p>
                            <p className="text-[11px] font-semibold text-gray-800">{formatMoney(ticket.price)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  <section>
                    <h3 className="text-xs uppercase tracking-widest text-gray-500 font-black mb-2">Payment Status</h3>
                    <div className="rounded-xl border border-gray-200 p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Current status</span>
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold ${statusClassName(selectedOrder.status)}`}>
                          {statusLabel(selectedOrder.status)}
                        </span>
                      </div>
                      {isPendingOrder(selectedOrder.status) ? (
                        <p className="text-[11px] text-gray-500">
                          This checkout is unpaid. Cancelling it will not issue tickets.
                        </p>
                      ) : null}
                      {isRefundedOrder(selectedOrder.status) ? (
                        <p className="text-[11px] text-gray-500">
                          This order is refunded in Echo Jham. Complete money return in the PayMongo dashboard if needed.
                        </p>
                      ) : null}
                      {isPaidOrder(selectedOrder.status) && selectedRefundRequest ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 space-y-1">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-amber-800">Refund request</p>
                          <p className="text-xs text-amber-900">{selectedRefundRequest.ticketCode} · {selectedRefundRequest.status}</p>
                          <p className="text-xs text-amber-900">{selectedRefundRequest.subject}</p>
                          {selectedRefundRequest.description ? (
                            <p className="text-[11px] text-amber-800 whitespace-pre-wrap break-words">
                              {selectedRefundRequest.description}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                      {isPaidOrder(selectedOrder.status) ? (
                        <div className="space-y-2">
                          <p className="text-[11px] text-gray-500">
                            Marking refunded invalidates tickets here. Refund the payment in PayMongo separately.
                          </p>
                          {selectedHasUsedTickets ? (
                            <label className="flex items-start gap-2 text-[11px] text-gray-700">
                              <input
                                type="checkbox"
                                checked={refundConfirmAck}
                                onChange={(event) => setRefundConfirmAck(event.target.checked)}
                                className="mt-0.5"
                              />
                              <span>I understand one or more tickets were already used.</span>
                            </label>
                          ) : null}
                          {selectedRefundRequest ? (
                            <div className="space-y-2">
                              <label className="block text-[11px] text-gray-600" htmlFor="deny-refund-note">
                                Deny note
                              </label>
                              <textarea
                                id="deny-refund-note"
                                value={denyNote}
                                onChange={(event) => setDenyNote(event.target.value)}
                                rows={2}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-xs text-gray-800"
                                placeholder="Reason for denying this refund request"
                              />
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </section>
                </div>

                {isPendingOrder(selectedOrder.status) ? (
                  <div className="sticky bottom-0 border-t border-gray-200 bg-white/95 backdrop-blur px-5 py-3">
                    <button
                      type="button"
                      onClick={cancelPendingCheckout}
                      disabled={isCancelling}
                      className="h-8 px-3 rounded bg-gray-800 text-white text-xs font-bold hover:bg-black disabled:opacity-60"
                    >
                      {isCancelling ? "Cancelling..." : "Cancel order"}
                    </button>
                  </div>
                ) : null}

                {isPaidOrder(selectedOrder.status) ? (
                  <div className="sticky bottom-0 border-t border-gray-200 bg-white/95 backdrop-blur px-5 py-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={requestMarkOrderRefunded}
                      disabled={isRefunding || isDenyingRefund || (selectedHasUsedTickets && !refundConfirmAck)}
                      className="h-8 px-3 rounded bg-[#8b0000] text-white text-xs font-bold hover:bg-[#6f0000] disabled:opacity-60"
                    >
                      Mark refunded
                    </button>
                    {selectedRefundRequest ? (
                      <button
                        type="button"
                        onClick={requestDenyRefundRequest}
                        disabled={isDenyingRefund || isRefunding || !denyNote.trim()}
                        className="h-8 px-3 rounded border border-gray-300 bg-white text-gray-800 text-xs font-bold hover:bg-gray-50 disabled:opacity-60"
                      >
                        Deny refund request
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {refundActionConfirm && selectedOrder ? (
            <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
              <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
                <h3 className="text-base font-black text-gray-900 uppercase tracking-tight mb-2">
                  {refundActionConfirm === "mark" ? "Mark refunded?" : "Deny refund request?"}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {refundActionConfirm === "mark"
                    ? selectedHasUsedTickets
                      ? `${selectedOrder.orderCode} has used tickets. Mark this order refunded anyway? Tickets will stop working. Money return in PayMongo stays manual.`
                      : `Mark ${selectedOrder.orderCode} as refunded? Tickets will stop working. Money return in PayMongo stays manual.`
                    : `Deny refund request ${selectedRefundRequest?.ticketCode || ""} for ${selectedOrder.orderCode}? The order will stay paid.`}
                </p>
                <div className="mt-6 flex flex-wrap justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setRefundActionConfirm(null)}
                    disabled={isRefunding || isDenyingRefund}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={refundActionConfirm === "mark" ? confirmMarkOrderRefunded : confirmDenyRefundRequest}
                    disabled={isRefunding || isDenyingRefund}
                    className="px-4 py-2 rounded-lg bg-brand-maroon text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60"
                  >
                    {isRefunding || isDenyingRefund
                      ? "Working..."
                      : refundActionConfirm === "mark"
                        ? "Yes, mark refunded"
                        : "Yes, deny request"}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Orders;
